import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const scenarios = [
  {
    icon: 'ri-water-flash-line',
    title: '進水泡水',
    desc: 'iPhone / Android 手機不慎進水，開機黑屏或無法開機，硬碟資料仍有機會救援。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '螢幕破裂無法操作',
    desc: '螢幕龜裂或觸控失效，無法解鎖，維修至能正常開機狀態備份或轉移資料至另一台手機。',
  },
  {
    icon: 'ri-cpu-line',
    title: '主板故障 / 晶片損壞',
    desc: '會針對主機板受損的電路、電容或電源管理 IC 進行精準修復，目標是將主機板搶修至「可通電開機」的狀態。',
  },
  {
    icon: 'ri-error-warning-line',
    title: '系統崩潰 / 升級失敗',
    desc: 'iOS / Android 系統更新失敗或 OTA 升級出錯，造成無限重啟無法進入系統。',
  },
];

const process = [
  {
    step: '01',
    title: '免費諮詢診斷',
    desc: '攜機至店或透過 LINE 描述故障狀況，技師初步評估資料救援可行性，完全免費。',
  },
  {
    step: '02',
    title: '報價確認',
    desc: '根據裝置狀態與損壞程度提供詳細報價，確認費用後才開始施工，無隱藏費用。',
  },
  {
    step: '03',
    title: '專業資料提取',
    desc: '使用專業設備進行晶片讀取、系統修復或繞過鎖定，安全提取重要資料。',
  },
  {
    step: '04',
    title: '資料確認交付',
    desc: '救援完成後讓您確認資料完整性，提供安全傳輸方式，保障個人隱私。',
  },
];

const faqs = [
  {
    q: '手機進水後該怎麼辦？',
    a: '手機進水後請立即：\n\n1. 馬上關機，不要嘗試開機或充電\n2. 不要用吹風機吹乾（熱風會加速氧化腐蝕）\n3. 盡快送至專業維修店處理\n\n進水後越快處理，資料救援成功率越高。切勿自行拆機或放入米中，這些方式無法有效去除腐蝕，反而可能讓情況更嚴重。',
  },
  {
    q: '手機摔壞、螢幕破裂，資料還救得回來嗎？',
    a: '螢幕破裂或觸控失效通常只是「顯示層」的問題，資料儲存晶片位於主機板上，通常不會隨螢幕損壞而消失。即便手機摔到無法開機，只要儲存晶片本身沒有嚴重的物理碎裂，資料都有機會救回。',
  },
  {
    q: '資料救援的時間大約多久？',
    a: '資料救援時間會依裝置機板受損程度而有所不同，以下為參考區間：\n\n輕微受損：約 1–3 個工作天\n中度受損：約 3–7 個工作天\n嚴重受損：約 3–14 個工作天\n\n工程師檢測後，會說明預估時間；若救援過程需延長，我們會提前告知，並每 2–5 個工作天主動回報進度。',
  },
  {
    q: '該如何送修？',
    a: '1. 可親自至門市 台北市萬大路 251 號 兄弟維修收件\n2. 快遞上門收件，回程包裹會上封條保證雙方權益\n\n無論哪種方式，裝置送達後會先進行專業檢測，確認狀況與救援可行性，再向您回報。',
  },
  {
    q: '救援失敗還需要付費嗎？',
    a: '不需要。我們採「救得到才收費」政策，若無法成功救援您所需的重要資料，診斷費用另行說明，不收取救援費用。',
  },
  {
    q: '資料救援過程隱私有保障嗎？',
    a: '絕對有。我們嚴格保守客戶隱私，每個維修崗位皆配置24小時監控，保障雙方權益，技師只針對目標資料進行操作，救援完成後不留存任何客戶個人資料，請放心交機。',
  },
  {
    q: '哪些資料可以救援？',
    a: '包含照片與影片、通訊錄、LINE / WhatsApp 聊天記錄、備忘錄、文件檔案等。具體可救援項目依裝置狀態而定，歡迎帶機到店免費診斷。',
  },
  {
    q: '手機沒有備份，資料還有辦法救回來嗎？',
    a: '只要儲存晶片（硬碟）未損壞，資料都有救！\n\n我們並非直接讀取晶片內容，而是透過精密的硬體維修技術，將受損的主機板修復至「可開機狀態」。只要手機恢復通電運作，資料即可完整救回。\n\n建議：故障後請勿反覆嘗試開機，建議盡速送往門市，由維修師為您的主機板進行完整檢測。',
  },
  {
    q: '手機摔爛無法開機，資料還救得回來嗎？',
    a: '救回機率：約 60%\n\n強烈撞擊通常會導致主機板受損。若受損部位避開了儲存資料的核心 IC（如 CPU 或硬碟），救回機率會大幅提升。\n\n維修建議：建議盡快送檢，避免因外殼變形持續擠壓內部零件。',
  },
  {
    q: '手機不慎泡水（受潮）開不了機怎麼辦？',
    a: '救回機率：約 70%\n\n泡水後必須第一時間拆解主機板，進行專業超音波清洗，清除電路板上的雜質與氧化物。若清洗後仍無法開機，通常是主機板線路燒毀或重要 IC 損壞。\n\n緊急提醒：泡水後請勿嘗試充電或開機，以免造成二度短路。',
  },
  {
    q: '手機用一用突然無法開機，是什麼原因？',
    a: '救回機率：約 80%\n\n這類狀況通常最樂觀。可能是電池老化、系統當機，或是前幾日輕微摔到累積的損傷所致。這類問題通常修復耗時較短，成功率也最高。',
  },
  {
    q: '我的手機已經給別間修過了，還能再送修嗎？（二修 / 三修機）',
    a: '救回機率：約 20%\n\n經過多次拆修的手機，救援難度極高。因為標準維修流程大同小異，若前一家維修站無法修復，通常代表損壞點非常隱蔽或核心零件已報廢。',
  },
  {
    q: '手機還在保固內，資料重要但原廠說會清空資料，怎麼辦？',
    a: '原廠維修原則上「不保證留存資料」。\n\n如果您的資料（照片、LINE、通訊錄）極為重要，建議先送至我們這裡進行初步檢測與資料備份。待確認資料救回後，再決定是否送回原廠更換整新機，以確保「保固」與「珍貴資料」雙贏。',
  },
];

const pricing = [
  {
    label: '軟體資料救援',
    sub: '系統崩潰、軟體故障',
    price: 'NT$1,500 起',
    color: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-600',
  },
  {
    label: '進水 / 硬體故障救援',
    sub: '主板維修 + 資料提取',
    price: 'NT$3,000 起',
    color: 'bg-gray-50 border-gray-200',
    textColor: 'text-gray-700',
  },
  {
    label: '晶片級讀取救援',
    sub: '嚴重損壞、無法開機機型',
    price: '依實際評估報價',
    color: 'bg-gray-50 border-gray-200',
    textColor: 'text-gray-700',
  },
];

export default function DataRecoveryPage() {
  useSEO({
    title: '手機資料救援｜進水不開機・螢幕破裂・主板故障 救得到才收費',
    description: '兄弟維修提供 iPhone、Samsung、ASUS、Google Pixel 手機資料救援服務。進水泡水、螢幕破裂、主板故障、系統崩潰皆可處理。免費診斷，救得到才收費，台北萬華到店或快遞寄修。',
    keywords: '手機資料救援,iPhone資料救援,手機進水救援,不開機資料救回,台北資料救援,手機資料恢復',
    canonical: '/services/data-recovery',
  });

  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gray-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-25">
            <img
              src="https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/drpage-hero.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/80 to-gray-950/50" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-5">
                <span className="w-8 h-px bg-orange-400" />
                手機資料救援
                <span className="w-8 h-px bg-orange-400" />
              </div>
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                手機資料救援<br />
                <span className="text-orange-400">救得到才收費</span>
              </h1>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                iPhone、Android 全品牌手機資料救援服務。進水、螢幕破裂、系統崩潰、主板故障——無論什麼情況，我們都會盡全力找回您最重要的回憶。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  免費諮詢資料救援
                </button>
                <a
                  href="https://lin.ee/mOf6eFn"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="px-8 py-3.5 border border-white/30 hover:border-orange-400 text-white hover:text-orange-400 font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-line-fill mr-2" />
                  LINE 立即詢問
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Badges */}
        <section className="bg-orange-500 py-5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              {[
                { icon: 'ri-shield-check-line', text: '救得到才收費' },
                { icon: 'ri-search-eye-line', text: '免費到店診斷' },
                { icon: 'ri-lock-line', text: '隱私絕對保密' },
                { icon: 'ri-time-line', text: '最快當天完成' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 text-white font-medium text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${b.icon} text-white text-base`} />
                  </div>
                  {b.text}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
                <span className="w-6 h-px bg-orange-400" />
                常見救援情況
                <span className="w-6 h-px bg-orange-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">哪些情況需要資料救援？</h2>
              <p className="text-gray-500 max-w-xl mx-auto">不管是意外損壞還是誤操作，只要資料還沒被完全覆寫，就有機會找回來</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios.map((s) => (
                <div key={s.title} className="bg-gray-50 rounded-2xl p-6 hover:bg-orange-50 transition-colors group">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-4 group-hover:bg-orange-100 transition-colors">
                    <i className={`${s.icon} text-orange-500 text-xl`} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Supported Devices */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-white mb-2">支援機型</h2>
              <p className="text-gray-400 text-sm">全品牌、全系列手機資料救援</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: 'ri-smartphone-line', brand: 'iPhone', models: 'iPhone 6S ～ iPhone 17 全系列' },
                { icon: 'ri-android-line', brand: 'Samsung', models: 'Galaxy S / A / Note / Z 系列' },
                { icon: 'ri-gamepad-line', brand: 'ASUS', models: 'ZenFone / ROG Phone 系列' },
                { icon: 'ri-google-line', brand: 'Google Pixel', models: 'Pixel 3 ～ Pixel 10 全系列' },
              ].map((d) => (
                <div key={d.brand} className="bg-gray-800/50 rounded-2xl p-5 text-center border border-gray-700/50">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                    <i className={`${d.icon} text-orange-400 text-2xl`} />
                  </div>
                  <div className="text-white font-semibold text-sm mb-1">{d.brand}</div>
                  <div className="text-gray-400 text-xs">{d.models}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
                <span className="w-6 h-px bg-orange-400" />
                服務流程
                <span className="w-6 h-px bg-orange-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">資料救援服務流程</h2>
              <p className="text-gray-500">透明、專業、以客戶利益為優先</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {process.map((p, i) => (
                <div key={p.step} className="relative">
                  {i < process.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-px border-t-2 border-dashed border-orange-200 z-0" style={{ width: 'calc(100% - 2rem)', left: '3rem' }} />
                  )}
                  <div className="relative z-10 bg-gray-50 rounded-2xl p-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-orange-500 rounded-xl mb-4">
                      <span className="text-white font-bold text-lg">{p.step}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
                <span className="w-6 h-px bg-orange-400" />
                費用說明
                <span className="w-6 h-px bg-orange-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">資料救援費用</h2>
              <p className="text-gray-500">免費診斷評估，確認可行後再報價，救不到不收費</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {pricing.map((p) => (
                <div key={p.label} className={`rounded-2xl border p-6 ${p.color}`}>
                  <div className="font-bold text-gray-900 mb-1">{p.label}</div>
                  <div className="text-gray-400 text-xs mb-4">{p.sub}</div>
                  <div className={`text-2xl font-bold ${p.textColor}`}>{p.price}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">
              實際費用依裝置損壞程度與資料量而定，建議攜機到店免費評估
            </p>
          </div>
        </section>

        {/* Visual Split: Image + CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/drpage-lab.jpg"
                  alt="手機資料救援專業環境"
                  className="w-full h-80 object-cover object-top rounded-2xl"
                />
              </div>
              <div>
                <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-500 text-xs font-medium px-3 py-1 rounded-full mb-4">
                  <i className="ri-verified-badge-line text-sm" />
                  專業設備 · 安全可靠
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-5">為什麼選擇兄弟維修做資料救援？</h2>
                <ul className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '救得到才收費', desc: '救援失敗不收取救援費用，診斷完全免費，無前置費用壓力。' },
                    { icon: 'ri-tools-line', title: '專業晶片級設備', desc: '備有精密顯微焊接與專用主機板診斷儀器，專治各種不開機、死機等一般店家無法處理的硬體電路問題。' },
                    { icon: 'ri-lock-2-line', title: '隱私安全保障', desc: '救援過程嚴格保密，不留存任何客戶個人資料，讓您放心交機。' },
                    { icon: 'ri-customer-service-2-line', title: '即時溝通回報', desc: '施工過程主動通知進度，隨時可透過 LINE 詢問狀態，不讓您擔心等待。' },
                  ].map((item) => (
                    <li key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-orange-500 text-base`} />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                        <div className="text-gray-500 text-sm mt-0.5">{item.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
                <span className="w-6 h-px bg-orange-400" />
                常見問題
                <span className="w-6 h-px bg-orange-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">資料救援 Q&amp;A</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={faq.q} className="bg-white rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                  >
                    <span className="font-medium text-gray-900 text-sm pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-orange-500`} />
                    </div>
                  </button>
                  <div className={`${openFaq === i ? 'block' : 'hidden'} px-6 pb-5`}>
                    <p className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">資料還在，就還有機會</h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              發生意外後不要驚慌，盡快停止使用手機避免資料被覆蓋。立即聯絡我們，免費評估救援可行性。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                立即免費諮詢
              </button>
              <a
                href="tel:0916858251"
                className="px-8 py-3.5 border border-gray-600 hover:border-orange-400 text-gray-300 hover:text-orange-400 font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                <i className="ri-phone-line mr-2" />
                0916-858-251
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
